import { MYDRA_WORKSPACE_ID_COOKIE_NAME } from '@/config/system'
import { useUserContext } from '@/shared/contexts/userContext'
import {
  WorkspaceContext,
  WorkspaceContextType,
} from '@/shared/contexts/workspaceContext'
import Cookies from 'js-cookie'
import React, { useEffect, useState } from 'react'

const COOKIE_LIFETIME_IN_DAYS = 30

type WorkspaceProviderProps = {
  children: React.ReactNode
  overrideWorkspaceId?: string
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({
  children,
  overrideWorkspaceId,
}) => {
  const [workspaceId, _setWorkspaceId] = useState<string>('')
  const [isInitialized, setInitialized] = useState(false)

  const {
    user: contextUser,
    isLoading: isLoadingUser,
    error: userError,
  } = useUserContext()

  // Only use user data if not overriding workspace ID
  const user = overrideWorkspaceId ? null : contextUser

  const { workspaces = [] } = user ?? {}
  const workspaceIds = workspaces.map((workspace) => workspace._id)

  useEffect(() => {
    if (isInitialized) {
      return
    }

    // If overrideWorkspaceId is provided, use it and skip cookie logic
    if (overrideWorkspaceId) {
      _setWorkspaceId(overrideWorkspaceId)
      setInitialized(true)
      return
    }

    const cookieWorkspaceId = Cookies.get(MYDRA_WORKSPACE_ID_COOKIE_NAME)

    if (cookieWorkspaceId) {
      _setWorkspaceId(cookieWorkspaceId)
      setInitialized(true)
      return
    }

    if (isLoadingUser) {
      return
    }

    if (user && Array.isArray(workspaceIds) && workspaceIds.length > 0) {
      const [firstWorkspaceId = ''] = workspaceIds
      _setWorkspaceId(firstWorkspaceId)
      Cookies.set(MYDRA_WORKSPACE_ID_COOKIE_NAME, firstWorkspaceId, {
        expires: COOKIE_LIFETIME_IN_DAYS,
      })
    }

    setInitialized(true)
  }, [
    isInitialized,
    isLoadingUser,
    userError,
    _setWorkspaceId,
    workspaceIds,
    user,
    overrideWorkspaceId,
  ])

  const setWorkspaceId = (id: string) => {
    _setWorkspaceId(id)
    // Only write to cookie if not using override
    if (!overrideWorkspaceId) {
      Cookies.set(MYDRA_WORKSPACE_ID_COOKIE_NAME, id, {
        expires: COOKIE_LIFETIME_IN_DAYS,
      })
    }
  }

  const clearWorkspaceId = () => {
    _setWorkspaceId(null)
    // Only remove cookie if not using override
    if (!overrideWorkspaceId) {
      Cookies.remove(MYDRA_WORKSPACE_ID_COOKIE_NAME)
    }
  }

  if (!isInitialized) {
    return null
  }

  const contextValue: WorkspaceContextType = {
    workspaceId,
    setWorkspaceId,
    clearWorkspaceId,
    isLoading: isLoadingUser,
    error: userError,
  }

  return (
    <WorkspaceContext.Provider value={contextValue}>
      {children}
    </WorkspaceContext.Provider>
  )
}
